import { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, Link } from 'react-router-dom';
import api from '../../lib/api';
import { authApi } from '../../lib/auth-api';

type RequestStatus = 'idle' | 'pending' | 'running' | 'completed' | 'failed';

// Streaming arches use SSE instead of polling
const STREAMING_ARCHES = ['arche_make_a_request'];

interface NodeResult {
  nodeId: string;
  entityName: string | null;
  status: string;
  output: Record<string, unknown> | null;
}

interface RequestResult {
  status: RequestStatus;
  output: Record<string, unknown> | null;
  nodes: NodeResult[];
  errorMessage: string | null;
}

export function RunArchePage() {
  const { id } = useParams<{ id: string }>();
  const [arche, setArche] = useState<{ name: string; simpleDescription: string } | null>(null);
  const [query, setQuery] = useState('');
  const [status, setStatus] = useState<RequestStatus>('idle');
  const [result, setResult] = useState<RequestResult | null>(null);
  const [requestId, setRequestId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [streamingText, setStreamingText] = useState('');
  const abortControllerRef = useRef<AbortController | null>(null);

  // Fetch arche details
  useEffect(() => {
    if (!id) return;
    api.getMarketplaceArche(id)
      .then((data) => {
        setArche({ name: data.name, simpleDescription: data.simpleDescription });
      })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [id]);

  // Poll for results
  const pollStatus = useCallback(async () => {
    if (!requestId) return;

    try {
      const data = await api.getRequestStatus(requestId);
      setResult({
        status: data.status as RequestStatus,
        output: data.output,
        nodes: data.nodes.map(n => ({
          nodeId: n.nodeId,
          entityName: n.entityName,
          status: n.status,
          output: n.output,
        })),
        errorMessage: data.errorMessage,
      });

      if (data.status === 'completed' || data.status === 'failed') {
        setStatus(data.status as RequestStatus);
        return true; // Stop polling
      }
      return false;
    } catch (e) {
      console.error('Failed to poll status:', e);
      return true; // Stop on error
    }
  }, [requestId]);

  useEffect(() => {
    if (!requestId || status === 'completed' || status === 'failed') return;

    const interval = setInterval(async () => {
      const done = await pollStatus();
      if (done) clearInterval(interval);
    }, 1000);

    // Initial poll
    pollStatus();

    return () => clearInterval(interval);
  }, [requestId, status, pollStatus]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!id || !query.trim()) return;

    setStatus('pending');
    setResult(null);
    setStreamingText('');

    // Use streaming for supported arches
    if (STREAMING_ARCHES.includes(id)) {
      try {
        setStatus('running');
        abortControllerRef.current = new AbortController();

        // Get a fresh token (auto-refreshes if expired)
        const authToken = await authApi.getValidAccessToken();
        const response = await fetch(`/v1/arches/${id}/stream`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...(authToken && { 'Authorization': `Bearer ${authToken}` }),
          },
          body: JSON.stringify({ input: { query: query.trim() } }),
          signal: abortControllerRef.current.signal,
        });

        const reader = response.body?.getReader();
        const decoder = new TextDecoder();
        let fullText = '';

        if (reader) {
          while (true) {
            const { done, value } = await reader.read();
            if (done) break;

            const text = decoder.decode(value);
            const lines = text.split('\n');

            for (const line of lines) {
              if (line.startsWith('data: ')) {
                try {
                  const data = JSON.parse(line.slice(6));
                  if (data.type === 'chunk') {
                    fullText += data.content;
                    setStreamingText(fullText);
                  } else if (data.type === 'done') {
                    setStatus('completed');
                    setResult({
                      status: 'completed',
                      output: { summary: fullText },
                      nodes: [],
                      errorMessage: null,
                    });
                  } else if (data.type === 'error') {
                    setStatus('failed');
                    setResult({
                      status: 'failed',
                      output: null,
                      nodes: [],
                      errorMessage: data.message,
                    });
                  }
                } catch {
                  // Ignore parse errors
                }
              }
            }
          }
        }
      } catch (err) {
        if ((err as Error).name !== 'AbortError') {
          console.error('Streaming failed:', err);
          setStatus('failed');
        }
      }
      return;
    }

    // Non-streaming arches use polling
    try {
      const data = await api.startArche(id, { query: query.trim() });
      setRequestId(data.requestId);
      setStatus('running');
    } catch (e) {
      console.error('Failed to start arche:', e);
      setStatus('failed');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
      </div>
    );
  }

  if (!arche) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <p className="text-gray-500">Arche not found</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b">
        <div className="max-w-3xl mx-auto px-4 py-6">
          <Link to={`/marketplace/arches/${id}`} className="text-blue-600 hover:text-blue-800 text-sm mb-2 inline-block">
            &larr; Back to details
          </Link>
          <h1 className="text-2xl font-bold text-gray-900">{arche.name}</h1>
          <p className="text-gray-600 mt-1">{arche.simpleDescription}</p>
        </div>
      </div>

      {/* Main content */}
      <div className="max-w-3xl mx-auto px-4 py-8">
        {/* Input form */}
        <form onSubmit={handleSubmit} className="bg-white rounded-lg shadow-sm border p-6 mb-6">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            What would you like to know?
          </label>
          <textarea
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="e.g., How do I get started with Fias?"
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
            rows={3}
            disabled={status === 'pending' || status === 'running'}
          />
          <button
            type="submit"
            disabled={!query.trim() || status === 'pending' || status === 'running'}
            className="mt-4 w-full bg-blue-600 text-white py-3 px-6 rounded-lg font-medium hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {status === 'pending' || status === 'running' ? (
              <span className="flex items-center justify-center gap-2">
                <span className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />
                Processing...
              </span>
            ) : (
              'Get Answer'
            )}
          </button>
        </form>

        {/* Streaming response (during and after completion) */}
        {streamingText && (
          <div className="bg-white rounded-lg shadow-sm border overflow-hidden mb-6">
            <div className={`border-b px-6 py-3 ${status === 'completed' ? 'bg-green-50 border-green-100' : 'bg-blue-50 border-blue-100'}`}>
              <span className={`font-medium flex items-center gap-2 ${status === 'completed' ? 'text-green-700' : 'text-blue-700'}`}>
                {status === 'completed' ? (
                  <>✓ Response</>
                ) : (
                  <><span className="animate-pulse">●</span> Generating response...</>
                )}
              </span>
            </div>
            <div className="p-6">
              <p className="text-gray-700 leading-relaxed whitespace-pre-wrap">
                {streamingText}
                {status === 'running' && <span className="animate-pulse">▊</span>}
              </p>
            </div>
          </div>
        )}

        {/* Progress indicator */}
        {(status === 'pending' || status === 'running') && !streamingText && result?.nodes && (
          <div className="bg-white rounded-lg shadow-sm border p-6 mb-6">
            <h3 className="text-sm font-medium text-gray-700 mb-4">Progress</h3>
            <div className="space-y-3">
              {result.nodes.map((node, idx) => (
                <div key={node.nodeId} className="flex items-center gap-3">
                  <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-medium ${
                    node.status === 'completed' ? 'bg-green-100 text-green-700' :
                    node.status === 'running' ? 'bg-blue-100 text-blue-700 animate-pulse' :
                    node.status === 'failed' ? 'bg-red-100 text-red-700' :
                    'bg-gray-100 text-gray-500'
                  }`}>
                    {node.status === 'completed' ? '✓' : idx + 1}
                  </div>
                  <span className={`text-sm ${node.status === 'running' ? 'text-blue-700 font-medium' : 'text-gray-600'}`}>
                    {node.entityName || `Step ${idx + 1}`}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Results (for non-streaming arches only) */}
        {status === 'completed' && result && !streamingText && (
          <div className="bg-white rounded-lg shadow-sm border overflow-hidden">
            <div className="bg-green-50 border-b border-green-100 px-6 py-3">
              <span className="text-green-700 font-medium">Results</span>
            </div>
            <div className="p-6">
              {/* Show documents from first node if available */}
              {Array.isArray(result.nodes[0]?.output?.documents) && (
                <div className="mb-6">
                  <h4 className="text-sm font-medium text-gray-700 mb-3">Related Documents</h4>
                  <ul className="space-y-2">
                    {(result.nodes[0].output!.documents as string[]).map((doc, idx) => (
                      <li key={idx} className="flex items-center gap-2 text-gray-700">
                        <span className="text-blue-500">📄</span>
                        {doc}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Show summary from output */}
              {typeof result.output?.summary === 'string' && (
                <div>
                  <h4 className="text-sm font-medium text-gray-700 mb-3">Summary</h4>
                  <p className="text-gray-700 leading-relaxed bg-gray-50 rounded-lg p-4">
                    {result.output.summary}
                  </p>
                </div>
              )}

              {/* Fallback: show raw output if no structured data */}
              {!Array.isArray(result.nodes[0]?.output?.documents) && typeof result.output?.summary !== 'string' && result.output && (
                <pre className="text-sm text-gray-700 bg-gray-50 rounded-lg p-4 overflow-auto">
                  {JSON.stringify(result.output, null, 2)}
                </pre>
              )}
            </div>
          </div>
        )}

        {/* Error state */}
        {status === 'failed' && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-6">
            <p className="text-red-700">
              {result?.errorMessage || 'Something went wrong. Please try again.'}
            </p>
          </div>
        )}
      </div>
    </div>
  );
}

export default RunArchePage;
